import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import {
  formatMoney,
  useInvoicePayments,
  useRecordPayment,
  useCancelInvoice,
  useRefundInvoice,
  type InvoiceRow,
} from '@/hooks/useInvoices';

interface PaymentManagementProps {
  invoice: InvoiceRow;
  canManage: boolean;
}

export function PaymentManagement({ invoice, canManage }: PaymentManagementProps) {
  const { t } = useTranslation();
  const { data: payments, isLoading } = useInvoicePayments(invoice.id);
  const recordPayment = useRecordPayment();
  const cancelInvoice = useCancelInvoice();
  const refundInvoice = useRefundInvoice();

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);

  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [paymentDate, setPaymentDate] = useState(
    new Date().toISOString().split('T')[0]
  );

  const [cancelReason, setCancelReason] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');

  const totalPaid = invoice.total_paid_cents ?? 0;
  const remainingBalance = invoice.total_cents - totalPaid;
  const isPayable = ['issued', 'partial', 'overdue'].includes(invoice.status);
  const isCancellable = !['paid', 'void', 'cancelled', 'refunded'].includes(invoice.status);
  const isRefundable = ['paid', 'partial'].includes(invoice.status) && totalPaid > 0;

  const handleRecordPayment = async () => {
    const amountCents = Math.round(parseFloat(paymentAmount) * 100);
    if (isNaN(amountCents) || amountCents <= 0) {
      toast.error(t('invoices.payment_invalid_amount', 'Invalid payment amount'));
      return;
    }

    if (amountCents > remainingBalance) {
      toast.error(
        t('invoices.payment_exceeds_balance', 'Payment exceeds remaining balance')
      );
      return;
    }

    try {
      await recordPayment.mutateAsync({
        invoice_id: invoice.id,
        amount_cents: amountCents,
        payment_date: new Date(paymentDate).toISOString(),
        payment_method: paymentMethod || undefined,
        payment_reference: paymentReference || undefined,
        notes: paymentNotes || undefined,
      });
      toast.success(t('invoices.payment_recorded', 'Payment recorded successfully'));
      setShowPaymentModal(false);
      setPaymentAmount('');
      setPaymentMethod('');
      setPaymentReference('');
      setPaymentNotes('');
    } catch (err: any) {
      toast.error(err.message || t('invoices.payment_error', 'Failed to record payment'));
    }
  };

  const handleCancel = async () => {
    if (!cancelReason.trim()) {
      toast.error(t('invoices.cancel_reason_required', 'Cancellation reason is required'));
      return;
    }

    try {
      await cancelInvoice.mutateAsync({
        invoice_id: invoice.id,
        reason: cancelReason,
      });
      toast.success(t('invoices.cancelled', 'Invoice cancelled'));
      setShowCancelModal(false);
      setCancelReason('');
    } catch (err: any) {
      toast.error(err.message || t('invoices.cancel_error', 'Failed to cancel invoice'));
    }
  };

  const handleRefund = async () => {
    if (!refundReason.trim()) {
      toast.error(t('invoices.refund_reason_required', 'Refund reason is required'));
      return;
    }

    const refundCents = refundAmount
      ? Math.round(parseFloat(refundAmount) * 100)
      : totalPaid;

    if (isNaN(refundCents) || refundCents <= 0) {
      toast.error(t('invoices.refund_invalid_amount', 'Invalid refund amount'));
      return;
    }

    if (refundCents > totalPaid) {
      toast.error(
        t('invoices.refund_exceeds_paid', 'Refund amount exceeds total paid')
      );
      return;
    }

    try {
      await refundInvoice.mutateAsync({
        invoice_id: invoice.id,
        reason: refundReason,
        refund_amount_cents: refundAmount ? refundCents : undefined,
      });
      toast.success(t('invoices.refunded', 'Invoice refunded'));
      setShowRefundModal(false);
      setRefundAmount('');
      setRefundReason('');
    } catch (err: any) {
      toast.error(err.message || t('invoices.refund_error', 'Failed to refund invoice'));
    }
  };

  return (
    <div className="space-y-4">
      {/* Payment Summary */}
      <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-card-dark">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">
            {t('invoices.payment_status', 'Payment Status')}
          </h3>
          {canManage && isPayable && (
            <Button
              size="sm"
              onClick={() => setShowPaymentModal(true)}
              disabled={remainingBalance <= 0}
            >
              <Plus className="mr-1 h-4 w-4" />
              {t('invoices.record_payment', 'Record Payment')}
            </Button>
          )}
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <dt className="text-sm text-gray-500 dark:text-gray-400">
              {t('invoices.total_amount', 'Total Amount')}
            </dt>
            <dd className="mt-1 text-xl font-semibold text-gray-900 dark:text-white">
              {formatMoney(invoice.total_cents, invoice.currency)}
            </dd>
          </div>
          <div>
            <dt className="text-sm text-gray-500 dark:text-gray-400">
              {t('invoices.amount_paid', 'Amount Paid')}
            </dt>
            <dd className="mt-1 text-xl font-semibold text-emerald-600 dark:text-emerald-400">
              {formatMoney(totalPaid, invoice.currency)}
            </dd>
          </div>
          <div>
            <dt className="text-sm text-gray-500 dark:text-gray-400">
              {t('invoices.remaining_balance', 'Remaining Balance')}
            </dt>
            <dd className="mt-1 text-xl font-semibold text-orange-600 dark:text-orange-400">
              {formatMoney(remainingBalance, invoice.currency)}
            </dd>
          </div>
        </div>

        {invoice.status === 'overdue' && (
          <div className="mt-3 flex items-center gap-2 rounded-md bg-orange-50 p-3 text-sm text-orange-800 dark:bg-orange-900/20 dark:text-orange-200">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>
              {t('invoices.overdue_message', 'This invoice is overdue. Due date:')}{' '}
              {invoice.due_at ? new Date(invoice.due_at).toLocaleDateString('da-DK') : '—'}
            </span>
          </div>
        )}
      </div>

      {/* Payment History */}
      <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-card-dark">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white">
          {t('invoices.payment_history', 'Payment History')}
        </h3>

        {isLoading ? (
          <div className="mt-4 flex justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
          </div>
        ) : !payments || payments.length === 0 ? (
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            {t('invoices.no_payments', 'No payments recorded yet')}
          </p>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead>
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {t('invoices.payment_date', 'Date')}
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {t('invoices.payment_amount', 'Amount')}
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {t('invoices.payment_method_label', 'Method')}
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {t('invoices.payment_reference', 'Reference')}
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {t('invoices.payment_notes', 'Notes')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {payments.map((payment) => (
                  <tr key={payment.id}>
                    <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-900 dark:text-white">
                      {new Date(payment.payment_date).toLocaleDateString('da-DK')}
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-sm font-medium text-gray-900 dark:text-white">
                      {formatMoney(payment.amount_cents, payment.currency)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500 dark:text-gray-400">
                      {payment.payment_method || '—'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500 dark:text-gray-400">
                      {payment.payment_reference || '—'}
                    </td>
                    <td className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">
                      {payment.notes || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      {canManage && (
        <div className="flex flex-wrap gap-2">
          {isCancellable && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowCancelModal(true)}
            >
              {t('invoices.cancel_invoice', 'Cancel Invoice')}
            </Button>
          )}
          {isRefundable && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowRefundModal(true)}
            >
              {t('invoices.refund_invoice', 'Refund Invoice')}
            </Button>
          )}
        </div>
      )}

      {/* Record Payment Modal */}
      {showPaymentModal && (
        <Modal
          onClose={() => setShowPaymentModal(false)}
          title={t('invoices.record_payment', 'Record Payment')}
        >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('invoices.payment_amount', 'Amount')} ({invoice.currency})
            </label>
            <Input
              type="number"
              step="0.01"
              min="0"
              max={remainingBalance / 100}
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(e.target.value)}
              placeholder={`Max: ${(remainingBalance / 100).toFixed(2)}`}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {t('invoices.remaining_balance', 'Remaining Balance')}:{' '}
              {formatMoney(remainingBalance, invoice.currency)}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('invoices.payment_date', 'Payment Date')}
            </label>
            <Input
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('invoices.payment_method_label', 'Payment Method')} ({t('common.optional', 'Optional')})
            </label>
            <Input
              type="text"
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              placeholder={t('invoices.payment_method_placeholder', 'e.g., Bank Transfer, Cash, Card')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('invoices.payment_reference', 'Reference')} ({t('common.optional', 'Optional')})
            </label>
            <Input
              type="text"
              value={paymentReference}
              onChange={(e) => setPaymentReference(e.target.value)}
              placeholder={t('invoices.payment_reference_placeholder', 'e.g., Transaction ID')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('invoices.payment_notes', 'Notes')} ({t('common.optional', 'Optional')})
            </label>
            <textarea
              rows={3}
              value={paymentNotes}
              onChange={(e) => setPaymentNotes(e.target.value)}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              placeholder={t('invoices.payment_notes_placeholder', 'Additional notes about this payment')}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="secondary" onClick={() => setShowPaymentModal(false)}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button onClick={handleRecordPayment} disabled={recordPayment.isPending}>
              {recordPayment.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('invoices.record_payment', 'Record Payment')}
            </Button>
          </div>
        </div>
        </Modal>
      )}

      {/* Cancel Invoice Modal */}
      {showCancelModal && (
        <Modal
          onClose={() => setShowCancelModal(false)}
          title={t('invoices.cancel_invoice', 'Cancel Invoice')}
        >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('invoices.cancel_invoice_warning', 'This will mark the invoice as cancelled. This action cannot be undone.')}
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('invoices.cancel_reason', 'Reason for Cancellation')}
            </label>
            <textarea
              rows={3}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              placeholder={t('invoices.cancel_reason_placeholder', 'Why is this invoice being cancelled?')}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="secondary" onClick={() => setShowCancelModal(false)}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button onClick={handleCancel} disabled={cancelInvoice.isPending}>
              {cancelInvoice.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('invoices.cancel_invoice', 'Cancel Invoice')}
            </Button>
          </div>
        </div>
        </Modal>
      )}

      {/* Refund Invoice Modal */}
      {showRefundModal && (
        <Modal
          onClose={() => setShowRefundModal(false)}
          title={t('invoices.refund_invoice', 'Refund Invoice')}
        >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('invoices.refund_invoice_warning', 'This will record a refund and update the invoice status.')}
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('invoices.refund_amount', 'Refund Amount')} ({invoice.currency})
            </label>
            <Input
              type="number"
              step="0.01"
              min="0"
              max={totalPaid / 100}
              value={refundAmount}
              onChange={(e) => setRefundAmount(e.target.value)}
              placeholder={`${t('invoices.full_refund', 'Full refund')}: ${(totalPaid / 100).toFixed(2)}`}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {t('invoices.leave_empty_full_refund', 'Leave empty to refund the full amount paid')}: {formatMoney(totalPaid, invoice.currency)}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('invoices.refund_reason', 'Reason for Refund')}
            </label>
            <textarea
              rows={3}
              value={refundReason}
              onChange={(e) => setRefundReason(e.target.value)}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              placeholder={t('invoices.refund_reason_placeholder', 'Why is this invoice being refunded?')}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="secondary" onClick={() => setShowRefundModal(false)}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button onClick={handleRefund} disabled={refundInvoice.isPending}>
              {refundInvoice.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('invoices.refund_invoice', 'Refund Invoice')}
            </Button>
          </div>
        </div>
        </Modal>
      )}
    </div>
  );
}
